/**
 * Google Drive Client
 *
 * Frontend client for Google Drive API operations.
 */

import type { GoogleDriveFile, GoogleDrivePermission } from '../domain/google';

import { getApiEndpoints } from '../config';

const getApiBase = () => `${getApiEndpoints().google}/drive`;

/** JWT token for gateway audit logging */
function getJwtHeaders(): Record<string, string> {
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      const token = parsed.accessToken || parsed.token;
      if (token) return { 'Authorization': `Bearer ${token}` };
    }
  } catch { /* ignore */ }
  return {};
}

/** Helper to make authenticated requests */
async function driveFetch<T>(
  endpoint: string,
  sessionId: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${getApiBase()}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Google-Session': sessionId,
      ...getJwtHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Drive API request failed');
  }

  return data;
}

/**
 * List files and folders
 */
export async function listDriveFiles(
  sessionId: string,
  options: {
    pageSize?: number;
    pageToken?: string;
    query?: string;
    orderBy?: string;
  } = {}
): Promise<{
  files: GoogleDriveFile[];
  nextPageToken?: string;
}> {
  const params = new URLSearchParams();
  if (options.pageSize) params.set('pageSize', String(options.pageSize));
  if (options.pageToken) params.set('pageToken', options.pageToken);
  if (options.query) params.set('q', options.query);
  if (options.orderBy) params.set('orderBy', options.orderBy);

  const queryString = params.toString();
  const data = await driveFetch<any>(
    `/files${queryString ? `?${queryString}` : ''}`,
    sessionId
  );

  return {
    files: data.files || [],
    nextPageToken: data.nextPageToken,
  };
}

/**
 * Get file metadata
 */
export async function getDriveFile(
  sessionId: string,
  fileId: string
): Promise<GoogleDriveFile> {
  const data = await driveFetch<any>(`/files/${fileId}`, sessionId);
  return data.file;
}

/**
 * Download file content
 * Returns a Blob that can be used to create a download link
 */
export async function downloadDriveFile(
  sessionId: string,
  fileId: string
): Promise<Blob> {
  const response = await fetch(`${getApiBase()}/files/${fileId}/content`, {
    headers: {
      'X-Google-Session': sessionId,
      ...getJwtHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download file');
  }

  return response.blob();
}

/**
 * Create a folder
 */
export async function createDriveFolder(
  sessionId: string,
  name: string,
  parentId?: string
): Promise<GoogleDriveFile> {
  const data = await driveFetch<any>('/folders', sessionId, {
    method: 'POST',
    body: JSON.stringify({ name, parentId }),
  });

  return data.folder;
}

/**
 * Upload a file to Drive
 */
export async function uploadDriveFile(
  sessionId: string,
  file: File,
  folderId?: string
): Promise<GoogleDriveFile> {
  const formData = new FormData();
  formData.append('file', file);
  if (folderId) {
    formData.append('folderId', folderId);
  }

  const response = await fetch(`${getApiBase()}/upload`, {
    method: 'POST',
    headers: {
      'X-Google-Session': sessionId,
      ...getJwtHeaders(),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Drive upload failed');
  }

  const data = await response.json();
  return data.file;
}

/**
 * Share a file
 */
export async function shareDriveFile(
  sessionId: string,
  fileId: string,
  email: string,
  role: 'reader' | 'writer' | 'commenter' = 'reader',
  sendNotification: boolean = true
): Promise<GoogleDrivePermission> {
  const data = await driveFetch<any>(`/files/${fileId}/share`, sessionId, {
    method: 'POST',
    body: JSON.stringify({
      email,
      role,
      type: 'user',
      sendNotificationEmail: sendNotification,
    }),
  });

  return data.permission;
}

/**
 * Move file to trash
 */
export async function trashDriveFile(
  sessionId: string,
  fileId: string
): Promise<void> {
  await driveFetch<any>(`/files/${fileId}`, sessionId, {
    method: 'DELETE',
  });
}

/**
 * Search files by name
 */
export async function searchDriveFiles(
  sessionId: string,
  name: string,
  options: {
    type?: 'folder' | 'document' | 'spreadsheet' | 'all';
    pageSize?: number;
  } = {}
): Promise<GoogleDriveFile[]> {
  const params = new URLSearchParams();
  params.set('name', name);
  if (options.type && options.type !== 'all') params.set('type', options.type);
  if (options.pageSize) params.set('pageSize', String(options.pageSize));

  const data = await driveFetch<any>(`/search?${params.toString()}`, sessionId);
  return data.files || [];
}

/**
 * List files in a specific folder
 */
export async function listFolderContents(
  sessionId: string,
  folderId: string,
  options: {
    pageSize?: number;
    pageToken?: string;
  } = {}
): Promise<{
  files: GoogleDriveFile[];
  nextPageToken?: string;
}> {
  return listDriveFiles(sessionId, {
    ...options,
    query: `'${folderId}' in parents and trashed = false`,
  });
}

/**
 * Get recent files
 */
export async function getRecentDriveFiles(
  sessionId: string,
  maxResults: number = 20
): Promise<GoogleDriveFile[]> {
  const { files } = await listDriveFiles(sessionId, {
    pageSize: maxResults,
    query: 'trashed = false',
    orderBy: 'modifiedTime desc',
  });

  return files;
}

/**
 * Get starred files
 */
export async function getStarredDriveFiles(
  sessionId: string,
  maxResults: number = 20
): Promise<GoogleDriveFile[]> {
  const { files } = await listDriveFiles(sessionId, {
    pageSize: maxResults,
    query: 'starred = true and trashed = false',
  });

  return files;
}

/**
 * Check if file is a Google Workspace type
 */
export function isGoogleWorkspaceFile(file: GoogleDriveFile): boolean {
  return file.mimeType.startsWith('application/vnd.google-apps');
}

/**
 * Get file icon based on mime type
 */
export function getDriveFileIcon(mimeType: string): string {
  if (mimeType === 'application/vnd.google-apps.folder') return 'folder';
  if (mimeType === 'application/vnd.google-apps.document') return 'file-text';
  if (mimeType === 'application/vnd.google-apps.spreadsheet') return 'table';
  if (mimeType === 'application/vnd.google-apps.presentation') return 'presentation';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'music';
  if (mimeType === 'application/pdf') return 'file-pdf';
  return 'file';
}
